import React, { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { api, fileUrl } from "@/lib/api";
import { Quote as QuoteIcon, User } from "lucide-react";

export const TestimonialsSection = () => {
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [initialized, setInitialized] = useState(false);
  const [loaded, setLoaded] = useState(false);

  if (!initialized) {
    setInitialized(true);
    api
      .get("/testimonials")
      .then((res) => setItems(res.data || []))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }

  if (!loaded || items.length === 0) return null;

  return (
    <section
      data-testid="testimonials-section"
      className="border-t border-zinc-800 px-6 md:px-12 lg:px-16 py-24 md:py-32 bg-[#0d0d10]"
    >
      <div className="max-w-3xl mb-16">
        <div className="text-xs tracking-[0.3em] uppercase text-[#ff5a00] font-mono mb-3">
          / {t.testimonials.eyebrow}
        </div>
        <h2 className="font-display text-5xl md:text-7xl uppercase leading-none">
          {t.testimonials.title}
        </h2>
        <p className="mt-6 text-zinc-400 text-base md:text-lg">
          {t.testimonials.lead}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((it, i) => (
          <article
            key={it.id}
            data-testid={`testimonial-card-${it.id}`}
            className="relative border border-zinc-800 bg-[#09090b] p-8 group hover:border-[#ff5a00] transition-colors"
          >
            <QuoteIcon
              className="absolute top-6 right-6 text-zinc-800 group-hover:text-[#ff5a00]/30 transition-colors"
              size={48}
              strokeWidth={1}
            />
            <div className="relative">
              <div className="text-[10px] font-mono tracking-[0.3em] text-zinc-600 mb-6">
                / {String(i + 1).padStart(2, "0")}
              </div>
              <blockquote className="text-zinc-200 text-base md:text-lg leading-relaxed">
                <span className="text-[#ff5a00] font-display text-3xl leading-none align-top mr-1">
                  &ldquo;
                </span>
                {it.quote}
                <span className="text-[#ff5a00] font-display text-3xl leading-none ml-1">
                  &rdquo;
                </span>
              </blockquote>
              <div className="mt-8 pt-6 border-t border-zinc-800 flex items-center gap-4">
                <div className="w-12 h-12 bg-zinc-900 border border-zinc-800 flex items-center justify-center overflow-hidden shrink-0">
                  {it.image_url ? (
                    <img
                      src={fileUrl(it.image_url)}
                      alt={it.author_name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User size={18} className="text-zinc-600" />
                  )}
                </div>
                <div className="min-w-0">
                  <div className="font-display text-lg uppercase tracking-wide leading-tight truncate">
                    {it.author_name}
                  </div>
                  {it.author_role && (
                    <div className="text-xs text-zinc-500 font-mono uppercase tracking-widest truncate">
                      {it.author_role}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};
